import { InfiniteData, infiniteQueryOptions, UseInfiniteQueryOptions } from "@tanstack/react-query";
import { AnnotationStyle } from "~/components/build/Show/components/Annotation";
import { Build, CursorPaginated, Job } from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";

export type JobAnnotation = {
  id: string;
  body: { html: string | null | undefined };
  style: AnnotationStyle;
  context: string;
};

export const jobAnnotationsQueryKey = (build: Build, job: Job) => {
  return [
    "build",
    build.id,
    "jobs",
    job.id,
    "annotations",
    `count:${job.statistics.annotationsCount || 0}`,
  ];
};

type JobAnnotationsPage = CursorPaginated<JobAnnotation>;
type JobAnnotationsPageParam = string | null;
type JobAnnotationsQueryKey = ReturnType<typeof jobAnnotationsQueryKey>;

type AdditionalOptions = Omit<
  UseInfiniteQueryOptions<
    JobAnnotationsPage,
    Error,
    InfiniteData<JobAnnotationsPage, JobAnnotationsPageParam>,
    JobAnnotationsQueryKey,
    JobAnnotationsPageParam
  >,
  "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
>;

export const jobAnnotationsQueryOptions = (build: Build, job: Job, options?: AdditionalOptions) => {
  return infiniteQueryOptions({
    ...options,
    queryKey: jobAnnotationsQueryKey(build, job),
    queryFn: ({ pageParam }) => fetchJobAnnotations(build.buildDataBasePath, job.id, pageParam),
    initialPageParam: null as JobAnnotationsPageParam,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : null),
  });
};

function fetchJobAnnotations(
  buildDataBasePath: Build["buildDataBasePath"],
  jobId: Job["id"],
  pageParam: string | null,
) {
  const url = new URL(`${buildDataBasePath}/jobs/${jobId}/annotations`, window.location.origin);

  if (pageParam) {
    url.searchParams.set("after", pageParam);
  }

  return fetcher<CursorPaginated<JobAnnotation>>(url);
}
